import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAlertsOverviewFull } from '@/hooks/useAlerts'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/use-toast'
import { useWsChannel } from '@/components/WebSocketProvider'
import { AlertNotificationContent } from '@/components/notifications/AlertNotificationContent'

// Key to track the timestamp of the last seen alert to avoid duplicate notifications on reload
const LAST_SEEN_KEY = 'global-notifications-last-seen'

export function GlobalAlertsWatcher() {
    const location = useLocation()

    if (location.pathname !== '/') {
        return null
    }

    return <DashboardAlertsWatcher />
}

function DashboardAlertsWatcher() {
    const { toast, dismiss } = useToast()
    const { user, isLoading: isAuthLoading } = useAuth()
    const queryClient = useQueryClient()
    const alertNotificationsEnabled = user?.preferences?.alert_notifications_enabled !== false
    const lastSeenRef = useRef<number>(
        (() => {
            try {
                const saved = localStorage.getItem(LAST_SEEN_KEY)
                return saved ? parseInt(saved) : Date.now() / 1000
            } catch {
                return Date.now() / 1000
            }
        })()
    )

    // Keep track of which alert IDs we've already notified for in this session
    // This interacts with the timestamp check to be robust
    const notifiedAlertIds = useRef<Set<string>>(new Set())

    // Subscribe to alerts:overview WebSocket channel for instant updates
    useWsChannel('alerts:overview', () => {
        // When new alert data arrives via WS, invalidate the full overview query
        // so the existing data-processing logic below picks it up
        queryClient.invalidateQueries({ queryKey: ['alerts', 'overview', 'full'] })
    })

    // We only fetch high-level overview data to check for new items
    // Using a small window to catch recent alerts
    const { data } = useAlertsOverviewFull({
        time_range: '1h', // Only get recent alerts
    }, {
        enabled: true,
        refetchInterval: 30000, // Slowed to 30s — WS push triggers invalidation for real-time
    })

    useEffect(() => {
        if (isAuthLoading || !user) return
        if (!data?.alerts || data.alerts.length === 0) return
        if (!alertNotificationsEnabled) return

        // Sort alerts by timestamp descending (newest first)
        const sortedAlerts = [...data.alerts].sort((a, b) => {
            const tsA = Number(a.timestamp || a.first_seen_ts || 0)
            const tsB = Number(b.timestamp || b.first_seen_ts || 0)
            return tsB - tsA
        })

        const newestAlert = sortedAlerts[0]
        const newestTs = Number(newestAlert.timestamp || newestAlert.first_seen_ts || 0)

        // Initial load sync - don't notify for existing alerts, just update reference
        if (Date.now() / 1000 - newestTs > 300) { // If newest alert is older than 5 mins, just ignore
            // Update last seen if it's newer than what we have
            if (newestTs > lastSeenRef.current) {
                lastSeenRef.current = newestTs
                localStorage.setItem(LAST_SEEN_KEY, newestTs.toString())
            }
            return
        }

        // Check for new alerts
        const newAlerts = sortedAlerts.filter(alert => {
            const ts = Number(alert.timestamp || alert.first_seen_ts || 0)
            // Basic check: is it newer than our last seen timestamp?
            // And also have we NOT notified for it yet in this session? (deduplication)
            // We use a composite key for ID since alerts might not have a raw 'id' field in the overview
            const id = `${alert.alert_type}-${alert.exchange}-${alert.symbol}-${ts}`

            const isNew = ts > lastSeenRef.current && !notifiedAlertIds.current.has(id)
            return isNew
        })

        if (newAlerts.length > 0) {
            // Update last seen to the newest one
            lastSeenRef.current = newestTs
            localStorage.setItem(LAST_SEEN_KEY, newestTs.toString())

            // Notify for the newest ones (limit to 3 to avoid spamming on a batch)
            const alertsToNotify = newAlerts.slice(0, 3)

            alertsToNotify.forEach((alertToNotify) => {
                const ts = Number(alertToNotify.timestamp || alertToNotify.first_seen_ts || 0)
                const id = `${alertToNotify.alert_type}-${alertToNotify.exchange}-${alertToNotify.symbol}-${ts}`
                notifiedAlertIds.current.add(id)

                const { id: toastId } = toast({
                    description: (
                        <AlertNotificationContent
                            alert={alertToNotify as Record<string, unknown>}
                            onDismiss={() => dismiss(toastId)}
                        />
                    ),
                    duration: 15000, // 15 seconds for alert notifications
                    onClick: () => {
                        window.open('/', '_blank')
                        dismiss(toastId)
                    },
                })
            })
        }
    }, [data, toast, dismiss, isAuthLoading, user, alertNotificationsEnabled])

    return null
}
